// ─── Shared Tracking Core ───────────────────────────────────────────────────
// Common utilities used by visitorTracking, promoterTracking, and tokenTracking.
// Prevents duplication of UUID generation, cookie handling, and visitor ID lookup.
// ────────────────────────────────────────────────────────────────────────────

import { hasAnalyticsConsent } from "./consentManager";

/**
 * Generate a unique ID using crypto.randomUUID() or fallback UUID v4.
 * @returns {string}
 */
export const generateTrackingId = () => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

/**
 * Set a tracking cookie (only when analytics consent is given).
 * @param {string} name
 * @param {string} value
 * @param {number} maxAge - Seconds until expiry
 */
export const setTrackingCookie = (name, value, maxAge) => {
  if (!hasAnalyticsConsent()) return;
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${name}=${value}; path=/; max-age=${maxAge}; SameSite=Lax${secure}`;
};

/**
 * Read a value from a cookie by name.
 * @param {string} name
 * @returns {string|null}
 */
export const getTrackingCookie = (name) => {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]+)`));
  return match ? match[1] : null;
};

/**
 * Delete a tracking cookie.
 * @param {string} name
 */
export const clearTrackingCookie = (name) => {
  document.cookie = `${name}=; max-age=0; path=/; SameSite=Lax`;
};

/**
 * Get the current visitor ID from cookie or localStorage.
 * Used by promoterTracking and tokenTracking to attach clicks to visitors.
 * @returns {string|null}
 */
export const getVisitorId = () => {
  return (
    localStorage.getItem("visitorId") ||
    getTrackingCookie("visitorId") ||
    null
  );
};
